import serviceInspection from "./serviceInspection";

const emptySampling = () => ({
  method: "",
  samplePoints: null,
  assessedArea: null,
  distribution: "",
  notes: "",
});

const scheduleDiagnosis = (form) => {
  const current = Array.isArray(form.diagnosticos)
    ? form.diagnosticos[0] || {}
    : {};

  return {
    ...current,
    id:
      current.id ||
      `diagnostico-agenda-${Date.now()}-${Math.random()
        .toString(16)
        .slice(2)}`,
    talhaoId: current.talhaoId || "",
    nomeTalhao: form.field || current.nomeTalhao || "",
    culturaSelecionada:
      form.cultureId || current.culturaSelecionada || "",
    cultureName: form.crop || current.cultureName || "",
    variedade: current.variedade || "",
    estadioFenologico: current.estadioFenologico || "",
    umidadeSolo: current.umidadeSolo || "",
    mipSampling: { ...emptySampling(), ...(current.mipSampling || {}) },
    alvosPragas: current.alvosPragas || [],
    alvosDoencas: current.alvosDoencas || [],
    alvosDaninhas: current.alvosDaninhas || [],
    dadosAdicionais: current.dadosAdicionais || "",
    recomendacaoManejo: current.recomendacaoManejo || "",
    recommendedProducts: current.recommendedProducts || [],
    recipeId: current.recipeId || "",
  };
};

const scheduleToInspection = (form) => ({
  ...(form.id ? { id: form.id } : {}),
  reason: form.title || form.reason || "Visita técnica",
  data: form.date || form.data || new Date().toISOString().slice(0, 10),
  hora: form.startTime || form.hora || "08:00",
  idCliente: form.customerId || form.idCliente || null,
  customerName: form.customerName || "",
  idResponsavelTecnico:
    form.responsibleId || form.idResponsavelTecnico || null,
  professionalName: form.responsible || form.professionalName || "",
  idTalhao: form.propertyId || form.idTalhao || null,
  propertyName: form.property || form.propertyName || "",
  culturaSelecionada: form.cultureId || form.culturaSelecionada || null,
  cultureName: form.crop || form.cultureName || "",
  nomeTalhao: form.field || form.nomeTalhao || "",
  diagnosticos: [
    scheduleDiagnosis(form),
    ...(Array.isArray(form.diagnosticos) ? form.diagnosticos.slice(1) : []),
  ],
  status: statusToInspection(form.status || "Agendado"),
  dadosAdicionais: JSON.stringify({
    meta: true,
    type: form.type || "Visita técnica",
    priority: form.priority || "Normal",
    endTime: form.endTime || "",
    allDay: form.allDay || false,
    reminder: form.reminder || "",
    notes: form.notes || "",
  }),
});

const inspectionToSchedule = (insp) => {
  let meta = {};
  try {
    const raw = insp.dadosAdicionais || "";
    if (raw.startsWith("{")) {
      const parsed = JSON.parse(raw);
      if (parsed.meta) meta = parsed;
    }
  } catch (_) {
    meta = {};
  }
  const firstDiagnosis = Array.isArray(insp.diagnosticos)
    ? insp.diagnosticos[0] || {}
    : {};
  return {
    id: insp.id,
    commitmentId: insp.commitmentId || insp.id,
    title: insp.reason || meta.type || "Visita técnica",
    type: meta.type || "Visita técnica",
    date: insp.data,
    startTime: insp.hora || "08:00",
    endTime: meta.endTime || "",
    allDay: meta.allDay || false,
    responsible: insp.professionalName || "",
    priority: meta.priority || "Normal",
    status: statusToSchedule(insp.status),
    reminder: meta.reminder || "",
    notes: meta.notes || "",
    customerId: insp.customerId || insp.idCliente || "",
    customerName: insp.customerName || "",
    responsibleId: insp.idResponsavelTecnico || "",
    property: insp.property || insp.propertyName || "",
    propertyId: insp.propertyId || insp.idTalhao || "",
    cultureId:
      firstDiagnosis.culturaSelecionada ||
      insp.cultureId ||
      insp.culturaSelecionada ||
      "",
    crop:
      firstDiagnosis.cultureName || insp.crop || insp.cultureName || "",
    field: firstDiagnosis.nomeTalhao || insp.field || insp.nomeTalhao || "",
    diagnosticos: insp.diagnosticos || [],
    createdAt: insp.createdAt,
    updatedAt: insp.updatedAt,
  };
};

const statusToInspection = (s) => {
  const map = {
    Agendado: "Aberto",
    Confirmado: "Aberto",
    "Em atendimento": "Em andamento",
    Concluído: "Concluído",
    Reagendado: "Aberto",
    Cancelado: "Cancelado",
  };
  return map[s] || "Aberto";
};

const statusToSchedule = (s) => {
  const map = {
    Aberto: "Agendado",
    "Em andamento": "Em atendimento",
    Concluído: "Concluído",
    Cancelado: "Cancelado",
  };
  return map[s] || "Agendado";
};

export const listCommitments = async () => {
  try {
    const response = await serviceInspection.findAll();
    const items = response.data || [];
    return items.map(inspectionToSchedule);
  } catch (_) {
    return [];
  }
};

export const listAttendances = async () => {
  try {
    const response = await serviceInspection.findAll();
    const items = response.data || [];
    return items.filter(
      (i) => i.status === "Em andamento" || i.status === "Concluído"
    );
  } catch (_) {
    return [];
  }
};

export const saveCommitment = async (payload) => {
  if (payload.id) {
    const current = await serviceInspection.findById(payload.id);
    const scheduleFields = scheduleToInspection(payload);
    const merged = { ...current, ...scheduleFields, id: current.id };
    await serviceInspection.update(merged);
    const saved = await serviceInspection.findById(current.id);
    window.dispatchEvent(new CustomEvent("agronomic-agenda-updated"));
    return inspectionToSchedule(saved);
  }
  const inspection = scheduleToInspection(payload);
  inspection.id = await serviceInspection.save(inspection);
  const saved = await serviceInspection.findById(inspection.id);
  window.dispatchEvent(new CustomEvent("agronomic-agenda-updated"));
  return inspectionToSchedule(saved);
};

export const cancelCommitment = async (id) => {
  await serviceInspection.updateStatus(id, "Cancelado");
  window.dispatchEvent(new CustomEvent("agronomic-agenda-updated"));
};

export const startVisit = async (id) => {
  await serviceInspection.updateStatus(id, "Em andamento");
  const item = await serviceInspection.findById(id);
  window.dispatchEvent(new CustomEvent("agronomic-agenda-updated"));
  return inspectionToSchedule(item);
};
